import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { BiUser, BiMoney, BiTrendingUp, BiTrendingDown } from "react-icons/bi";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import AccountExportOptions from "../../AccountExportOptions";
import TableSkeleton from "../../TableSkeleton";
import { useAuth } from "../../../utils/AuthContext";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import Pagination from "../../Shared/Pagination";

const API_URL = "https://api.muktihospital.com/api/pathology-accounts/user-collection";
const PATHOLOGISTS_API_URL = "https://api.muktihospital.com/api/pathology-accounts/pathologists-list";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const BULK_CACHE_KEY = "pathology_account_summary_bulk_recent";

const userParamsToRange = (params) => {
  if (params.startDate && params.endDate) {
    return {
      start: new Date(new Date(params.startDate).setHours(0, 0, 0, 0)),
      end: new Date(new Date(params.endDate).setHours(23, 59, 59, 999)),
    };
  }
  if (params.year && params.month) {
    const y = parseInt(params.year, 10);
    const m = parseInt(params.month, 10);
    return {
      start: new Date(y, m - 1, 1, 0, 0, 0, 0),
      end: new Date(y, m, 0, 23, 59, 59, 999),
    };
  }
  if (params.year) {
    const y = parseInt(params.year, 10);
    return {
      start: new Date(y, 0, 1, 0, 0, 0, 0),
      end: new Date(y, 11, 31, 23, 59, 59, 999),
    };
  }
  return null;
};

// OVERLAP (not containment) — same fix as Refer/Doctor Ledger.
const isUserRangeInsideBulk = (range, bulk) => {
  if (!range || !bulk?.windowStart || !bulk?.windowEnd) return false;
  const ws = new Date(bulk.windowStart).getTime();
  const we = new Date(bulk.windowEnd).getTime();
  return range.start.getTime() <= we && range.end.getTime() >= ws;
};

/**
 * Build the User-wise Collection entirely from the bulk-recent cache, matching
 * what the /user-collection endpoint returns online. Mirrors the backend
 * aggregation exactly:
 *   - totalBusiness / totalDue / totalBills  -> from bills, keyed by bill.userName
 *   - totalCollected                          -> from payments, keyed by payment.userName
 *   - totalAgentPaid                          -> from agent withdrawals, keyed by withdrawal.userName
 *   - netCashInHand = totalCollected - totalAgentPaid
 */
const buildUserCollectionFromBulk = (range, bulk, opts) => {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const inRange = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime();
    return t >= startMs && t <= endMs;
  };
  const nameMatch = (n) =>
    !opts.userName ||
    (n && String(n).toLowerCase().includes(String(opts.userName).toLowerCase()));

  const summaryMap = {};
  const ensure = (name) => {
    if (!summaryMap[name]) {
      summaryMap[name] = {
        name,
        totalBusiness: 0,
        totalCollected: 0,
        totalDue: 0,
        totalAgentPaid: 0,
        netCashInHand: 0,
        totalBills: 0,
      };
    }
    return summaryMap[name];
  };

  // Visible bills: in range, not cancelled, and matching the user filter on
  // either the bill creator OR any of its payments (mirrors the backend OR).
  const matchedBills = [];
  for (const bill of bulk?.bills || []) {
    if (bill.status === "Cancelled") continue;
    if (!inRange(bill.createdAt)) continue;
    const billUserMatch = nameMatch(bill.userName);
    const payUserMatch = (bill.payments || []).some((p) => nameMatch(p.userName));
    if (opts.userName && !billUserMatch && !payUserMatch) continue;
    matchedBills.push(bill);

    const bname = bill.userName || "Unknown Staff";
    const s = ensure(bname);
    s.totalBusiness += Number(bill.netAmount) || 0;
    s.totalDue += Number(bill.dueAmount) || 0;
    s.totalBills += 1;
  }

  // Payments -> totalCollected (keyed by payment.userName, range on paymentDate).
  for (const bill of bulk?.bills || []) {
    if (bill.status === "Cancelled") continue;
    for (const p of bill.payments || []) {
      if (!inRange(p.paymentDate)) continue;
      if (opts.userName && !nameMatch(p.userName)) continue;
      const pname = p.userName || "Unknown Staff";
      ensure(pname).totalCollected += Number(p.amount) || 0;
    }
  }

  // Agent withdrawals -> totalAgentPaid (keyed by withdrawal.userName).
  for (const w of bulk?.withdrawals || []) {
    if (!inRange(w.createdAt)) continue;
    if (opts.userName && !nameMatch(w.userName)) continue;
    const wname = w.userName || "Unknown Staff";
    ensure(wname).totalAgentPaid += Number(w.amount) || 0;
  }

  Object.values(summaryMap).forEach((s) => {
    s.netCashInHand = s.totalCollected - s.totalAgentPaid;
  });

  matchedBills.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  const totalRecords = matchedBills.length;
  let pageRows = matchedBills;
  let totalPages = 1;
  if (opts.limit && opts.limit !== "0") {
    const limit = parseInt(opts.limit, 10) || 10;
    const page = parseInt(opts.page, 10) || 1;
    totalPages = Math.ceil(totalRecords / limit) || 1;
    pageRows = matchedBills.slice((page - 1) * limit, page * limit);
  }

  return {
    bills: pageRows,
    allBills: matchedBills,
    userSummary: Object.values(summaryMap),
    pagination: {
      totalRecords,
      totalPages,
      currentPage: parseInt(opts.page, 10) || 1,
    },
  };
};

const UserCollection = () => {
  const { user } = useAuth(); 

  const [loading, setLoading] = useState(false);
  const [bills, setBills] = useState([]);
  const [userSummary, setUserSummary] = useState([]);
  const [allBillsForExport, setAllBillsForExport] = useState([]);
  const [pathologists, setPathologists] = useState([]);
  const [bulkOfflineActive, setBulkOfflineActive] = useState(false);

  // --- PAGINATION STATES ---
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const itemsPerPage = 10;

  // --- FILTER STATES ---
  const [filterType, setFilterType] = useState("daily");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [searchUserName, setSearchUserName] = useState("");
  useEffect(() => {
    if (user?.name) setSearchUserName(user.name);
  }, [user?.name]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [payoutDetails, setPayoutDetails] = useState([]);

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const fetchPathologists = useCallback(async () => {
    try {
      const response = await axios.get(PATHOLOGISTS_API_URL, {
        headers: { "x-api-key": API_KEY }
      });
      const list = response.data.data || [];
      setPathologists(list);
      saveToCache("pathology_pathologists_list", list);
    } catch (err) {
      const cached = await readFromCache("pathology_pathologists_list");
      if (cached && Array.isArray(cached)) {
        setPathologists(cached);
      } else {
        console.error("Failed to load active pathologists list");
      }
    }
  }, []);

  const fetchUserLedger = useCallback(async () => {
    setLoading(true);
    const params = {
      userName: searchUserName,
      page: currentPage,
      limit: itemsPerPage,
      type: filterType,
    };

    if (filterType === "daily") {
      params.startDate = startDate; params.endDate = endDate;
    } else if (filterType === "monthly") {
      const [y, m] = selectedMonth.split("-");
      params.year = y; params.month = m;
    } else if (filterType === "yearly") {
      params.year = selectedYear;
    }

    try {
      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: params,
      });

      setBills(response.data.bills || []);
      setUserSummary(response.data.userSummary || []);
      setPayoutDetails(response.data.payoutDetails || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
      setTotalRecords(response.data.pagination?.totalRecords || 0);
      const cacheKey = `pathology_user_collection_${JSON.stringify(params)}`;
      setBulkOfflineActive(false);
      saveToCache(cacheKey, {
        bills: response.data.bills || [],
        userSummary: response.data.userSummary || [],
        pagination: response.data.pagination || null,
      });
    } catch (err) {
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cacheKey = `pathology_user_collection_${JSON.stringify(params)}`;
        const cached = await readFromCache(cacheKey);
        if (cached && cached.bills) {
          // Exact filter combination was cached online — replay it.
          setBills(cached.bills || []);
          setUserSummary(cached.userSummary || []);
          setTotalPages(cached.pagination?.totalPages || 1);
          setTotalRecords(cached.pagination?.totalRecords || 0);
          setBulkOfflineActive(false);
          toast.info("Offline: showing cached user collection.");
        } else {
          // No per-filter cache — compute from the bulk-recent window.
          const bulk = await readFromCache(BULK_CACHE_KEY);
          const range = userParamsToRange(params);
          if (bulk && isUserRangeInsideBulk(range, bulk)) {
            const built = buildUserCollectionFromBulk(range, bulk, {
              userName: params.userName,
              page: params.page,
              limit: params.limit,
            });
            setBills(built.bills);
            setUserSummary(built.userSummary);
            setTotalPages(built.pagination.totalPages);
            setTotalRecords(built.pagination.totalRecords);
            setBulkOfflineActive(true);
            toast.info("Offline: showing computed user collection from last 3 months.");
          } else {
            setBills([]);
            setUserSummary([]);
            setTotalPages(1);
            setTotalRecords(0);
            setBulkOfflineActive(false);
            toast.info("Offline: only last 3 months of data is available. Please connect to internet for older records.");
          }
        }
      } else {
        console.error("User collection fetch error:", err);
        toast.error("Failed to load user collection data");
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear, searchUserName, currentPage]);

  const fetchFullDataForExport = useCallback(async () => {
    const params = { userName: searchUserName, type: filterType, limit: "0" };
    if (filterType === "daily") {
      params.startDate = startDate; params.endDate = endDate;
    } else if (filterType === "monthly") {
      const [y, m] = selectedMonth.split("-");
      params.year = y; params.month = m;
    } else if (filterType === "yearly") {
      params.year = selectedYear;
    }

    try {
      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: params,
      });
      setAllBillsForExport(response.data.bills || []);
    } catch (err) {
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const bulk = await readFromCache(BULK_CACHE_KEY);
        const range = userParamsToRange(params);
        if (bulk && isUserRangeInsideBulk(range, bulk)) {
          const built = buildUserCollectionFromBulk(range, bulk, {
            userName: params.userName,
            page: 1,
            limit: "0",
          });
          setAllBillsForExport(built.allBills || []);
        } else {
          setAllBillsForExport([]);
        }
      } else {
        console.error("Export fetch error:", err);
      }
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear, searchUserName]);

  useEffect(() => {
    fetchPathologists();
    fetchUserLedger();
    fetchFullDataForExport();
  }, [fetchPathologists, fetchUserLedger, fetchFullDataForExport]);

  const permanentStaffList = useMemo(() => {
    const defaultOption = [{ value: "", label: "All Staff / Pathologists" }];
    const seen = new Set();
    const options = [];
    for (const p of pathologists) {
      if (p?.name && !seen.has(p.name)) {
        seen.add(p.name);
        options.push({ value: p.name, label: p.name });
      }
    }
    for (const b of allBillsForExport) {
      const n = b?.userName;
      if (n && !seen.has(n)) {
        seen.add(n);
        options.push({ value: n, label: n });
      }
    }
    return [...defaultOption, ...options];
  }, [pathologists, allBillsForExport]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) setCurrentPage(page);
  };

  const headers = ["SL", "Date", "Bill No", "Patient Name", "Agent", "Net Bill", "Collected", "Due"];

  const payoutExportHeaders = ["SL", "Date", "Type", "Recipient", "Bill No", "Paid By", "Amount"];
  const payoutExportMapper = (po) => {
    const index = (payoutDetails || []).findIndex((x) => x === po);
    return [
      index + 1,
      po.date ? new Date(po.date).toLocaleDateString("en-GB") : "N/A",
      po.type || "-",
      po.recipient || "-",
      po.billNumber || "-",
      po.paidBy || "-",
      (Number(po.amount) || 0).toFixed(2),
    ].join("\t");
  };

  const dataMapper = (row) => {
    const index = allBillsForExport.findIndex(item => item.id === row.id);
    return [
      index + 1,
      new Date(row.createdAt).toLocaleDateString("en-GB"),
      row.billNumber,
      row.patientName,
      row.refer || "—",
      row.netAmount.toFixed(2),
      row.paymentAmount.toFixed(2),
      row.dueAmount.toFixed(2)
    ].join("\t");
  };

  const myCollection = userSummary.find((u) => u.name === user?.name);
  const myNetCash = myCollection ? Number(myCollection.netCashInHand || 0).toFixed(2) : "0.00";
  const exportSubTitle = `User-wise Collection Report  |  Filter: ${filterType.toUpperCase()}  |  ${user?.name || "Staff"} — Net Cash in Hand: BDT ${myNetCash}`;
  const breadcrumbs = useMemo(() => [{ label: "Dashboard", url: "/" }, { label: "Accounts" }, { label: "User-wise Collection" }], []);
  const skeletonColumns = Array(8).fill({ width: 100, height: 16 });

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="User-wise Collection" breadcrumbs={breadcrumbs} />

        {bulkOfflineActive && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
            <span>⚠️</span>
            <span>Offline mode — showing data from the last 3 months only. Connect to the internet for older records.</span>
          </div>
        )}
      </div>

      {/* --- Filter Section --- */}
      <div className="bg-white shadow-sm rounded-lg mb-6">
        <div className="border-b border-dashed border-gray-300 p-4 flex items-center justify-between gap-2 flex-wrap print-hidden">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-medium text-gray-700 text-nowrap">Staff Collection:</h2>
            <div className="w-64">
              <DynamicSelect
                options={permanentStaffList}
                value={permanentStaffList.find(o => o.value === searchUserName) || permanentStaffList[0]}
                onChange={(selected) => {
                  setSearchUserName(selected ? selected.value : "");
                  setCurrentPage(1);
                }}
                placeholder="Select Pathologist Member"
                isSearchable={true}
                isClearable={true}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-sm font-medium">Filter:</label>
            <div className="w-32">
              <DynamicSelect options={filterOptions} value={filterOptions.find((o) => o.value === filterType)} onChange={(o) => setFilterType(o.value)} isSearchable={false} />
            </div>

            {filterType === "daily" && (
              <div className="flex gap-2">
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border rounded-md p-2 text-sm outline-none w-36" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border rounded-md p-2 text-sm outline-none w-36" />
              </div>
            )}

            {filterType === "monthly" && (
              <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="border rounded-md p-2 text-sm outline-none" />
            )}

            {filterType === "yearly" && (
              <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="border rounded-md p-2 text-sm outline-none w-24" min="2000" max="2100" />
            )}

            <AccountExportOptions
              data={allBillsForExport}
              title="Staff Collection Report"
              subTitle={exportSubTitle}
              headers={headers}
              dataMapper={dataMapper}
              secondaryData={payoutDetails}
              secondaryTitle="Payout Details (Agent / Doctor / Performer)"
              secondaryHeaders={payoutExportHeaders}
              secondaryDataMapper={payoutExportMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen(!dropdownOpen)}
            />
          </div>
        </div>

        <div className="p-4 bg-gray-50 border-b">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {userSummary.length === 0 ? (
              <div className="col-span-full text-center py-4 bg-white text-gray-400 rounded-lg text-xs italic">No financial activity recorded for selected staff/period.</div>
            ) : (
              userSummary.map((user, idx) => (
                <div key={idx} className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex flex-col justify-between">
                  <div className="flex items-center justify-between border-b pb-2 mb-2">
                    <div className="flex items-center gap-1.5 font-bold text-gray-700 capitalize">
                      <BiUser className="text-blue-500" size={16} />
                      <span>{user.name?.toLowerCase()}</span>
                    </div>
                    <span className="text-[10px] font-black bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">{user.totalBills} Bills Created</span>
                  </div>
                  
                  <div className="space-y-1.5 text-xs text-gray-600">
                    <div className="flex justify-between">
                      <span>Total Business Booked:</span>
                      <span className="font-semibold text-gray-800">৳{(user.totalBusiness || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingUp /> Live Cash Collected:</span>
                      <span className="font-bold">৳{(user.totalCollected || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-500 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingDown /> Paid to Agents (Payout):</span>
                      <span className="font-bold">৳{(user.totalAgentPaid || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-500 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingDown /> Paid to Doctors (Payout):</span>
                      <span className="font-bold">৳{(user.totalDoctorPaid || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-500 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingDown /> Others Expense:</span>
                      <span className="font-bold">৳{(user.totalOthersExpense || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-500 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingDown /> Handed Over (Out):</span>
                      <span className="font-bold">৳{(user.totalHandoverOut || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-green-600 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingUp /> Received (In):</span>
                      <span className="font-bold">৳{(user.totalHandoverIn || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-red-500 font-medium">
                      <span className="flex items-center gap-0.5"><BiTrendingDown /> Performer Paid (Payout):</span>
                      <span className="font-bold">৳{(user.totalPerformerPaid || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-purple-600 font-bold border-t border-gray-100 pt-2 mt-2">
                      <span>Total Discount Given:</span>
                      <span>৳{(user.totalDiscount || 0).toFixed(2)}</span>
                    </div>

                    <div className="pl-3 text-[11px] text-gray-500 space-y-1 mt-1 bg-gray-50 p-2 rounded-md border border-dashed">
                      <div className="flex justify-between text-gray-700">
                        <span>Hospital Discount:</span>
                        <span className="font-semibold">৳{(user.discountHospital || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-blue-700">
                        <span>Doctor Discount:</span>
                        <span className="font-semibold">৳{(user.discountDoctor || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-orange-600">
                        <span>Agent Discount:</span>
                        <span className="font-semibold">৳{(user.discountAgent || 0).toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between text-amber-600 pb-1 border-b border-dashed">
                      <span>Unpaid Patient Due:</span>
                      <span className="font-semibold">৳{(user.totalDue || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm pt-1.5 font-black text-blue-700">
                      <span className="flex items-center gap-1"><BiMoney /> Net Cash in Hand:</span>
                      <span className="text-base">৳{(user.netCashInHand || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* --- Data Table --- */}
        <div className="p-4">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    <th className="p-3">SL</th>
                    <th className="p-3">Date</th>
                    <th className="p-3">Bill No</th>
                    <th className="p-3">Patient Name</th>
                    <th className="p-3">Agent</th>
                    <th className="p-3 text-right">Net Bill</th>
                    <th className="p-3 text-right">Collected</th>
                    <th className="p-3 text-right">Due</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {loading ? (
                    <TableSkeleton rowCount={itemsPerPage} columns={skeletonColumns} />
                  ) : bills.length === 0 ? (
                    <tr><td colSpan="8" className="p-10 text-center text-gray-400">No records found matching criteria.</td></tr>
                  ) : (
                    bills.map((bill, index) => (
                      <tr key={bill.id} className="border-b last:border-none hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-500">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                        <td className="p-3 text-gray-500">{new Date(bill.createdAt).toLocaleDateString("en-GB")}</td>
                        <td className="p-3 font-bold text-gray-800">{bill.billNumber}</td>
                        <td className="p-3 text-gray-700 capitalize">{bill.patientName?.toLowerCase()}</td>
                        <td className="p-3">
                          <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-[11px] font-bold border border-blue-100 capitalize">
                            {bill.refer?.toLowerCase() || "—"}
                          </span>
                        </td>
                        <td className="p-3 text-right font-semibold text-gray-700">৳ {bill.netAmount.toFixed(2)}</td>
                        <td className="p-3 text-right">
                            {(() => {
                              const specificStaffCash = bill.payments
                                ? bill.payments
                                    .filter(p => p.userName?.toLowerCase() === searchUserName?.toLowerCase())
                                    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
                                : 0;

                              const uniqueStaffCount = bill.payments 
                                ? new Set(bill.payments.map(p => p.userName?.toLowerCase())).size 
                                : 0;

                              const isFiltered = searchUserName && searchUserName !== "";
                              const displayAmount = isFiltered ? specificStaffCash : bill.paymentAmount;

                              return (
                                <>
                                  <div className="font-black text-sm flex flex-col items-end">
                                    {isFiltered ? (
                                      <div className="text-blue-600">
                                        ৳ {displayAmount.toFixed(2)}
                                        <span className="block text-[10px] font-normal text-gray-400 mt-0.5">
                                          (Bill Total: ৳{bill.paymentAmount.toFixed(0)})
                                        </span>
                                      </div>
                                    ) : (
                                      <div className="text-emerald-600 flex items-center gap-1.5 justify-end">
                                        <span>৳ {displayAmount.toFixed(2)}</span>
                                        {uniqueStaffCount > 1 && (
                                          <span 
                                            title={bill.payments.map(p => `${p.userName?.split(' ')[0] ?? 'Unknown'}: +৳${p.amount}`).join(', ')}
                                            className="bg-gray-100 hover:bg-gray-200 text-gray-500 border border-gray-300 text-[9px] px-1.5 py-0.5 rounded font-black transition cursor-help"
                                          >
                                            {uniqueStaffCount} Staffs
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                {Number(bill.discountAmount || bill.discount || 0) > 0 && (
                                  <div className="text-[10px] font-bold text-purple-600 mt-1 flex flex-col items-end leading-tight">
                                    <span>Discount: −৳{Number(bill.discountAmount || bill.discount || 0).toFixed(0)}</span>
                                    <span className="text-[9px] text-gray-400 font-normal capitalize">
                                      (Source: {bill.discountSource === 'doctor' 
                                        ? `Doctor (${bill.doctorName || 'Consultant'})` 
                                        : bill.discountSource === 'agent' 
                                          ? `Agent (${bill.refer || 'Refer Agent'})` 
                                          : bill.discountSource})
                                    </span>
                                  </div>
                                )}

                                  {isFiltered && (
                                    <div className="flex flex-col items-end gap-1 mt-1.5">
                                      {bill.payments && bill.payments
                                        .filter(pay => pay.userName?.toLowerCase() === searchUserName?.toLowerCase())
                                        .map((pay, pIdx) => (
                                          <div key={pIdx} className="bg-blue-600 text-white border-blue-600 text-[9px] px-1.5 py-0.5 rounded border font-black shadow-xs">
                                            <span className="capitalize">{pay.userName?.toLowerCase()?.split(' ')[0] ?? 'Unknown'}:</span>
                                            <span>+৳{pay.amount.toFixed(0)}</span>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                        </td>
                        <td className="p-3 text-right font-bold text-red-500">৳ {bill.dueAmount.toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

           {/* --- Reusable Pagination --- */}
            <div className="print-hidden">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={totalRecords}
                itemsPerPage={itemsPerPage}
                onPageChange={handlePageChange}
              />
            </div>
          </div>

          {/* --- Payout Details (Agent / Doctor / Performer) --- */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-xs mt-6">
            <h3 className="text-sm font-bold text-gray-700 px-4 py-3 border-b bg-gray-50">
              Payout Details — Agent / Doctor / Performer
              {searchUserName ? ` (Paid by ${searchUserName})` : ""}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-100 border-b text-[11px] font-bold text-gray-600 uppercase tracking-wider">
                    <th className="p-3">Date</th>
                    <th className="p-3">Type</th>
                    <th className="p-3">Recipient</th>
                    <th className="p-3">Bill No</th>
                    <th className="p-3">Paid By</th>
                    <th className="p-3 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="text-xs">
                  {payoutDetails.length === 0 ? (
                    <tr><td colSpan="6" className="p-8 text-center text-gray-400">No payouts in this period.</td></tr>
                  ) : (
                    payoutDetails.map((po, idx) => (
                      <tr key={idx} className="border-b last:border-none hover:bg-gray-50">
                        <td className="p-3 text-gray-500">{new Date(po.date).toLocaleDateString("en-GB")}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold border ${
                            po.type === "Agent" ? "bg-orange-50 text-orange-700 border-orange-200"
                            : po.type === "Doctor" ? "bg-blue-50 text-blue-700 border-blue-200"
                            : po.type === "Performer" ? "bg-purple-50 text-purple-700 border-purple-200"
                            : "bg-red-50 text-red-700 border-red-200"
                          }`}>{po.type}</span>
                        </td>
                        <td className="p-3 text-gray-700 capitalize">{po.recipient?.toLowerCase() || "—"}</td>
                        <td className="p-3 text-gray-500 font-mono text-[11px]">{po.billNumber || "—"}</td>
                        <td className="p-3 text-gray-600 capitalize">{po.paidBy?.toLowerCase() || "—"}</td>
                        <td className="p-3 text-right font-bold text-red-500">৳ {Number(po.amount).toFixed(2)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCollection;